import { useEffect, useRef, useState } from 'react';
import {
  operatorConsole,
  type PackBuilderPoolCategory,
  type PackBuilderUpdatePatch,
} from '../../../lib/api/operatorConsole';
import { Button } from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { RangeInput } from '../../../components/ui/RangeInput';
import { Select } from '../../../components/ui/Select';
import { InlineTextField } from '../InlineTextField';
import {
  SUPPORT_DISABLED_HINT,
  formatMoney,
  formatPercent,
  isStaged,
  isSupportDisabled,
} from '../packBuilderFormat';
import type { PackTabProps } from './packTabProps';

const MIN_RTP = 0.05;
const RTP_STEP = 0.01;
const LOCKED_HINT = 'Unpublish the pack to change price or RTP.';
const RESTOCKING_HINT = 'The pack is restocking — price and RTP are locked.';

const GRADE_TYPE_OPTIONS = [
  { value: '', label: 'Any (graded and raw)' },
  { value: 'graded', label: 'Graded only' },
  { value: 'ungraded', label: 'Raw only' },
];

const SECTION = 'space-y-3 rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-4';

export function DetailsTab({ pack, limits, currency, onPackChange, notify }: PackTabProps) {
  // price / stated_rtp / groups are editable only while the pack is staged:
  // enabled/restocking are live (backend 409, CONTRACT §4.8) and `disabled` is
  // support-controlled. Support-disabled goes further and locks the listing
  // fields too — UpdatePack refuses every attribute for it.
  const supportDisabled = isSupportDisabled(pack);
  const locked = !isStaged(pack);
  const lockedHint = supportDisabled
    ? SUPPORT_DISABLED_HINT
    : pack.availability === 'restocking'
      ? RESTOCKING_HINT
      : LOCKED_HINT;
  const packCurrency = pack.currency ?? currency;
  const [categories, setCategories] = useState<PackBuilderPoolCategory[]>([]);
  const [rtpDraft, setRtpDraft] = useState(pack.stated_rtp);
  const [savingRtp, setSavingRtp] = useState(false);
  const [savingCategories, setSavingCategories] = useState(false);
  // The ref, not the state, is the guard: two clicks can be delivered before
  // React has re-rendered the boxes as disabled.
  const savingCategoriesRef = useRef(false);

  useEffect(() => {
    setRtpDraft(pack.stated_rtp);
  }, [pack.stated_rtp]);

  useEffect(() => {
    let cancelled = false;
    // Every pool category (CONTRACT §4.12), not just those with a live pack.
    operatorConsole.packBuilder
      .listPoolCategories()
      .then((res) => {
        if (!cancelled) setCategories(res.pool_categories);
      })
      .catch(() => {
        if (!cancelled) notify('error', 'Could not load categories');
      });
    return () => {
      cancelled = true;
    };
  }, [notify]);

  async function save(patch: PackBuilderUpdatePatch, success: string) {
    try {
      const updated = await operatorConsole.packBuilder.updatePack(pack.id, patch);
      onPackChange(updated);
      notify('success', success);
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Update failed');
    }
  }

  async function savePrice(raw: string) {
    const next = Number(raw);
    if (!Number.isFinite(next) || next <= 0) {
      notify('error', 'Price must be greater than 0.');
      return;
    }
    await save({ price: Math.round(next * 100) / 100 }, 'Price saved');
  }

  async function handleRtpApply() {
    setSavingRtp(true);
    try {
      await save({ stated_rtp: rtpDraft }, 'RTP saved');
    } finally {
      setSavingRtp(false);
    }
  }

  // The next set is derived from the pack we currently hold, so a second toggle
  // fired before the PATCH answers would silently undo the first. The boxes stay
  // disabled until `onPackChange` has delivered the saved set.
  async function toggleCategory(id: number, checked: boolean) {
    if (savingCategoriesRef.current) return;
    const next = checked
      ? [...pack.pool_category_ids, id]
      : pack.pool_category_ids.filter((c) => c !== id);
    savingCategoriesRef.current = true;
    setSavingCategories(true);
    try {
      await save({ pool_category_ids: next }, 'Categories saved');
    } finally {
      savingCategoriesRef.current = false;
      setSavingCategories(false);
    }
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <section className={SECTION}>
        <h2 className="text-[13px] font-semibold text-[var(--co-text)]">Listing</h2>
        {supportDisabled && (
          <p className="text-[11px] italic text-[var(--co-muted)]">{SUPPORT_DISABLED_HINT}</p>
        )}
        <InlineTextField
          label="Title"
          value={pack.title}
          disabled={supportDisabled}
          onSave={(v) => save({ title: v }, 'Title saved')}
        />
        {/* No allowEmpty: PoolOffer validates description presence, so an empty
            draft is dropped here instead of bouncing off the backend. */}
        <InlineTextField
          label="Description"
          kind="textarea"
          value={pack.description ?? ''}
          placeholder="What is inside this pack"
          disabled={supportDisabled}
          onSave={(v) => save({ description: v }, 'Description saved')}
        />
        <InlineTextField
          label="Pack name"
          value={pack.badge_name ?? ''}
          allowEmpty
          placeholder="No pack name"
          disabled={supportDisabled}
          onSave={(v) => save({ badge_name: v }, v ? 'Pack name saved' : 'Pack name cleared')}
        />
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-[var(--co-muted)]">Grade type:</span>
          <Select
            aria-label="Grade type"
            value={pack.card_grade_type ?? ''}
            options={GRADE_TYPE_OPTIONS}
            disabled={supportDisabled}
            onChange={(v) =>
              void save(
                { card_grade_type: v === 'graded' || v === 'ungraded' ? v : null },
                'Grade type saved',
              )
            }
          />
        </div>
        <fieldset className="border-0 p-0">
          <legend className="text-[11px] text-[var(--co-muted)]">
            Categories (at least one before publishing)
          </legend>
          {categories.length === 0 ? (
            <p className="text-[11px] text-[var(--co-muted)]">No pool categories exist yet.</p>
          ) : (
            <div className="mt-1 grid gap-1.5 sm:grid-cols-2">
              {categories.map((c) => (
                <Checkbox
                  key={c.id}
                  aria-label={`Category: ${c.name}`}
                  label={c.name}
                  checked={pack.pool_category_ids.includes(c.id)}
                  disabled={supportDisabled || savingCategories}
                  onChange={(checked) => void toggleCategory(c.id, checked)}
                />
              ))}
            </div>
          )}
        </fieldset>
      </section>

      <section className={SECTION}>
        <h2 className="text-[13px] font-semibold text-[var(--co-text)]">Price &amp; RTP</h2>
        <InlineTextField
          label="Price"
          kind="number"
          value={String(pack.price)}
          suffix={packCurrency ?? undefined}
          min={0.01}
          step="0.01"
          disabled={locked}
          hint={lockedHint}
          onSave={savePrice}
        />
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-[var(--co-muted)]">RTP:</span>
            {locked && (
              <span className="text-[11px] italic text-[var(--co-muted)]">{lockedHint}</span>
            )}
          </div>
          <RangeInput
            aria-label="RTP"
            value={rtpDraft}
            min={MIN_RTP}
            max={limits.max_rtp}
            step={RTP_STEP}
            disabled={locked}
            onChange={setRtpDraft}
            format={(v) => formatPercent(v)}
          />
          <div className="mt-1 flex items-center gap-2">
            <Button
              type="button"
              onClick={() => void handleRtpApply()}
              disabled={locked || savingRtp || rtpDraft === pack.stated_rtp}
              className="px-3 py-1 text-[12px]"
            >
              Apply RTP
            </Button>
            {rtpDraft < limits.min_rtp_warn && (
              <span className="text-[11px] text-[var(--co-warning)]">
                Below {formatPercent(limits.min_rtp_warn)} — the validator will warn.
              </span>
            )}
          </div>
        </div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px]">
          <dt className="text-[var(--co-muted)]">Estimated value</dt>
          <dd className="font-mono">{formatMoney(pack.estimated_value, packCurrency)}</dd>
          <dt className="text-[var(--co-muted)]">Cards in pack</dt>
          <dd className="font-mono">{pack.items_count}</dd>
        </dl>
      </section>
    </div>
  );
}
